import { useEffect, useId, useState } from 'react';

import { Icon, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@/ui';

import type { Session } from '@/types';
import type { FC, KeyboardEvent, PointerEvent } from 'react';

import {
  FORTY_EIGHT_HOURS_MS,
  isSessionStarted,
  isWithinRescheduleWindow,
  MEMBER_RESCHEDULE_NOTICE,
  MEMBER_RESCHEDULE_STARTED_NOTICE,
} from '@/components/MentoCal/sessionTiming';
import { canRescheduleSession } from '@/features/appointments/sessionCapabilities';

const MAX_TIMEOUT_MS = 2_147_483_647;

interface RescheduleMenuItemProps {
  enforceMemberRescheduleMinimumNotice?: boolean;
  enforceMemberRescheduleStartBoundary?: boolean;
  handleOnClose: (event: Event | React.SyntheticEvent) => void;
  onReschedule?: (session: Session) => void;
  session: Session;
}

const RescheduleMenuItem: FC<RescheduleMenuItemProps> = ({
  enforceMemberRescheduleMinimumNotice = false,
  enforceMemberRescheduleStartBoundary = false,
  handleOnClose,
  onReschedule,
  session,
}) => {
  const descriptionId = useId();
  const [clock, setClock] = useState(Date.now);
  const now = Math.max(clock, Date.now());
  const isDisabledAt = (value: number) =>
    (enforceMemberRescheduleStartBoundary &&
      isSessionStarted(session.startedAt, value)) ||
    (enforceMemberRescheduleMinimumNotice &&
      isWithinRescheduleWindow(session.startedAt, value));
  const disabled = isDisabledAt(now);
  const disabledNotice =
    enforceMemberRescheduleStartBoundary &&
    isSessionStarted(session.startedAt, now)
      ? MEMBER_RESCHEDULE_STARTED_NOTICE
      : MEMBER_RESCHEDULE_NOTICE;

  useEffect(() => {
    if (
      !enforceMemberRescheduleMinimumNotice &&
      !enforceMemberRescheduleStartBoundary
    )
      return undefined;

    const start = new Date(session.startedAt).getTime();
    const boundary = enforceMemberRescheduleMinimumNotice
      ? start - FORTY_EIGHT_HOURS_MS
      : start;
    const remaining = boundary - Date.now();
    if (!Number.isFinite(start) || remaining <= 0) return undefined;

    const timer = window.setTimeout(
      () => setClock(Date.now()),
      Math.min(remaining, MAX_TIMEOUT_MS),
    );
    return () => window.clearTimeout(timer);
  }, [
    enforceMemberRescheduleMinimumNotice,
    enforceMemberRescheduleStartBoundary,
    session.startedAt,
    now,
  ]);

  if (!onReschedule || !canRescheduleSession(session)) return null;

  const blockPointerActivation = (event: PointerEvent<HTMLLIElement>) => {
    if (!isDisabledAt(Date.now())) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const blockKeyboardActivation = (event: KeyboardEvent<HTMLLIElement>) => {
    if (
      !isDisabledAt(Date.now()) ||
      (event.key !== 'Enter' && event.key !== ' ')
    )
      return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <MenuItem
      aria-describedby={disabled ? descriptionId : undefined}
      aria-disabled={disabled || undefined}
      aria-label="Reschedule"
      className={disabled ? 'cursor-not-allowed text-cement-400' : undefined}
      onClick={(event) => {
        event.stopPropagation();
        if (isDisabledAt(Date.now())) {
          event.preventDefault();
          return;
        }
        handleOnClose(event);
        onReschedule(session);
      }}
      onKeyDown={blockKeyboardActivation}
      onPointerDown={blockPointerActivation}
    >
      <Tooltip title={disabled ? disabledNotice : ''} describeChild>
        <span className="flex w-full items-center">
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          <ListItemText>Reschedule</ListItemText>
          {disabled && (
            <span className="sr-only" id={descriptionId}>
              {disabledNotice}
            </span>
          )}
        </span>
      </Tooltip>
    </MenuItem>
  );
};

export default RescheduleMenuItem;
export type { RescheduleMenuItemProps };
